/**
 * Admin API 服务模块 — 所有后端 Admin 接口调用封装
 *
 * 10 个端点：认证 / 品牌管理 / 订阅管理 / 仪表盘
 */

import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './client'
import type {
  Brand,
  BrandConfig,
  Subscription,
  DashboardStats,
  Store,
} from '@chaoyinli/shared'

// ===== 认证 =====

/** Admin 登录响应 */
export interface AdminLoginResponse {
  jwt: string
  username: string
  role: string
}

/**
 * 管理员登录
 * @param username 用户名
 * @param password 密码
 */
export function adminLogin(username: string, password: string): Promise<AdminLoginResponse> {
  return apiPost<AdminLoginResponse>('/admin/login', { username, password })
}

/**
 * 修改管理员密码（已登录状态下改自己的密码）
 * @param oldPassword 原密码
 * @param newPassword 新密码（至少 6 位）
 */
export function changeAdminPassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  return apiPost<void>('/admin/change-password', {
    old_password: oldPassword,
    new_password: newPassword,
  })
}

// ===== 品牌管理 =====

/** 创建品牌请求参数 */
export interface BrandCreateParams {
  name: string
  sub_brand?: string
  category?: string
  slogan?: string
  logo_url?: string
  trial_days?: number
  /** 品牌通用口令（8-12 位字母数字），留空则后端自动生成 */
  access_code?: string
}

/** 更新品牌请求参数 */
export interface BrandUpdateParams {
  name?: string
  /**
   * 品牌英文标识（3-64 位小写字母/数字/中划线，全局唯一）：
   * 作为客户入口参数 ?brand=<slug>。不传 = 不修改；传空字符串 = 清空（入口回落 UUID）
   */
  slug?: string
  sub_brand?: string
  category?: string
  slogan?: string
  logo_url?: string
  /** 总点数额度池（点），所有登录该门店的员工共用 */
  quota_total?: number
  /** 单点金额（分，1 元 = 100 分），用于记录 */
  unit_price?: number
  daily_quota?: number
  /** 试用开始日期（YYYY-MM-DD），传 null 表示清空 */
  trial_start?: string | null
  /** 试用结束日期（YYYY-MM-DD），传 null 表示清空 */
  trial_end?: string | null
  /** 订阅方案：monthly/quarterly/yearly，传 null 表示清空 */
  subscription_type?: string | null
  /** 订阅到期日期（YYYY-MM-DD），传 null 表示清空 */
  subscription_end?: string | null
  /** 品牌通用口令（员工登录密码，8-12 位字母数字），不传则不修改 */
  access_code?: string
  /**
   * 老板专属密码（最高权限，4-32 位且不含空格）：
   * 不传 = 不修改；传空字符串 = 清空（停用该品牌老板入口）
   */
  boss_password?: string
}

/** 品牌配置请求参数 */
export interface BrandConfigParams {
  tone_style: string
  forbidden_words: string
  required_tags: string
  image_style: string
  color_tone: string
  category_keywords: string
  /** H5 主题主色（#RRGGBB）；空串表示不修改 */
  primary_color?: string
  /** H5 主题辅色（#RRGGBB）；空串表示不修改 */
  secondary_color?: string
  /** 扩展配置：自由 key-value JSON；不传表示不修改，传 {} 表示清空 */
  extra_config?: Record<string, unknown>
}

/** 删除品牌响应 */
export interface DeleteBrandResponse {
  id: string
  name: string
}

/** 品牌详情（含配置） */
export type BrandDetail = Brand & { config?: BrandConfig | null }

/** 重置口令响应 */
export interface ResetAccessCodeResponse {
  access_code: string
}

/**
 * 二维码落地形态
 * - poster：扫码进 H5 海报页 /qr/<key>（推荐，可长按存图转发员工群）
 * - login ：扫码直达登录页 /login?brand=<key>（历史行为）
 */
export type QrVariant = 'poster' | 'login'

/**
 * 二维码响应
 *
 * 后端一次返回两种落地形态，前端切换无需二次请求。
 * qrcode_url / h5_url 为兼容字段，默认等于 login 形态。
 */
export interface QrcodeResponse {
  qrcode_url: string
  h5_url: string
  brand_name: string
  /** 兼容字段当前对应的形态（后端未升级时可能缺省） */
  variant?: QrVariant
  /** 登录页链接 */
  login_url?: string
  /** 登录页二维码（PNG Data URL） */
  login_qrcode_url?: string
  /** 海报页链接 */
  poster_url?: string
  /** 海报页二维码（PNG Data URL） */
  poster_qrcode_url?: string
}

/**
 * 获取品牌列表
 * @param status 状态筛选：trial | active | expired
 */
export function getBrands(status?: string): Promise<Brand[]> {
  const params: Record<string, any> = {}
  if (status) params.status = status
  return apiGet<Brand[]>('/admin/brands', params)
}

/**
 * 创建品牌
 */
export function createBrand(data: BrandCreateParams): Promise<Brand> {
  return apiPost<Brand>('/admin/brands', data)
}

/**
 * 获取品牌详情（含配置）
 * @param brandId 品牌 ID
 */
export function getBrandDetail(brandId: string): Promise<BrandDetail> {
  return apiGet<BrandDetail>(`/admin/brands/${brandId}`)
}

/**
 * 更新品牌信息
 * @param brandId 品牌 ID
 * @param data 更新字段
 */
export function updateBrand(brandId: string, data: BrandUpdateParams): Promise<Brand> {
  return apiPut<Brand>(`/admin/brands/${brandId}`, data)
}

/**
 * 删除品牌（软删除，后端置 deleted_at，历史数据保留）
 * @param brandId 品牌 ID
 */
export function deleteBrand(brandId: string): Promise<DeleteBrandResponse> {
  return apiDelete<DeleteBrandResponse>(`/admin/brands/${brandId}`)
}

/**
 * 重置品牌通用口令（返回新明文）
 * @param brandId 品牌 ID
 * @returns 新的明文口令（旧口令 7 天内仍有效，本次不做即时失效）
 */
export function resetAccessCode(brandId: string): Promise<ResetAccessCodeResponse> {
  return apiPut<ResetAccessCodeResponse>(`/admin/brands/${brandId}/reset-access-code`)
}

/**
 * 更新品牌配置（调性、禁用词等）
 * @param brandId 品牌 ID
 * @param config 配置参数
 */
export function updateBrandConfig(brandId: string, config: BrandConfigParams): Promise<BrandConfig> {
  return apiPut<BrandConfig>(`/admin/brands/${brandId}/config`, config)
}

/**
 * 获取品牌**通用入口**二维码
 *
 * 客户侧只有一个二维码，不再区分员工码 / 老板码：
 * 扫码后由输入的密码决定角色（员工口令 → 普通，老板密码 → 老板）。
 *
 * 响应同时包含海报页与登录页两种形态，调用方按需取用；
 * variant 只影响兼容字段 qrcode_url / h5_url 指向谁。
 *
 * @param brandId 品牌 ID
 * @param variant 兼容字段采用的形态，默认沿用后端默认值（login）
 */
export function getBrandQrcode(brandId: string, variant?: QrVariant): Promise<QrcodeResponse> {
  const params: Record<string, any> = {}
  if (variant) params.variant = variant
  return apiGet<QrcodeResponse>(`/admin/brands/${brandId}/qrcode`, params)
}

// ===== 门店管理（P0-1 门店独立化）=====

/** 创建门店请求参数 */
export interface StoreCreateParams {
  name: string
  city?: string
  address?: string
  /** 门店招牌菜（JSON 数组，空表示回落品牌级） */
  signature_dishes?: string[]
  sort_order?: number
  is_active?: boolean
}

/** 更新门店请求参数（全字段可选，只传要改的） */
export interface StoreUpdateParams {
  name?: string
  city?: string
  address?: string
  signature_dishes?: string[]
  sort_order?: number
  is_active?: boolean
}

/**
 * 获取品牌下所有门店（含禁用，admin 全量管理）
 * @param brandId 品牌 ID
 */
export function listStores(brandId: string): Promise<Store[]> {
  return apiGet<Store[]>(`/admin/brands/${brandId}/stores`)
}

/**
 * 新增门店
 * @param brandId 品牌 ID
 * @param data 门店字段
 */
export function createStore(brandId: string, data: StoreCreateParams): Promise<Store> {
  return apiPost<Store>(`/admin/brands/${brandId}/stores`, data)
}

/**
 * 更新门店（只改显式传入的字段）
 * @param brandId 品牌 ID
 * @param storeId 门店 ID
 * @param data 要更新的字段
 */
export function updateStore(
  brandId: string,
  storeId: string,
  data: StoreUpdateParams,
): Promise<Store> {
  return apiPut<Store>(`/admin/brands/${brandId}/stores/${storeId}`, data)
}

/**
 * 删除门店（物理删除）
 * @param brandId 品牌 ID
 * @param storeId 门店 ID
 */
export function deleteStore(brandId: string, storeId: string): Promise<void> {
  return apiDelete<void>(`/admin/brands/${brandId}/stores/${storeId}`)
}

// ===== 订阅管理 =====

/** 创建按点计费订单请求参数（服务商后台） */
export interface CreateSubscriptionParams {
  brand_id: string
  /** 单点金额（分），如 3 元/点 = 300 */
  unit_price: number
  /** 本次授予的点数（如 500） */
  quota_granted: number
}

/**
 * 创建订阅订单
 */
export function createSubscription(data: CreateSubscriptionParams): Promise<Subscription> {
  return apiPost<Subscription>('/admin/subscriptions', data)
}

/**
 * 确认到账
 * @param orderId 订单 ID
 * @param data 可选起始日期
 */
export function confirmPayment(
  orderId: string,
  data?: { start_date?: string },
): Promise<Subscription> {
  return apiPut<Subscription>(`/admin/subscriptions/${orderId}/confirm`, data)
}

/**
 * 获取按点计费订单列表
 */
export function getSubscriptions(params?: {
  brand_id?: string
  status?: string
  page?: number
  page_size?: number
}): Promise<{ total: number; page: number; page_size: number; items: Subscription[] }> {
  return apiGet('/admin/subscriptions', params)
}

/** 编辑待付款订单请求参数 */
export interface UpdateSubscriptionParams {
  unit_amount?: number
  quota_granted?: number
  amount?: number
  start_date?: string
  contract_url?: string
  invoice_status?: string
}

/**
 * 编辑待付款订单
 */
export function updateSubscription(
  orderId: string,
  data: UpdateSubscriptionParams,
): Promise<Subscription> {
  return apiPut<Subscription>(`/admin/subscriptions/${orderId}`, data)
}

/**
 * 删除/取消待付款订单
 */
export function deleteSubscription(orderId: string): Promise<Subscription> {
  return apiDelete<Subscription>(`/admin/subscriptions/${orderId}`)
}

// ===== 仪表盘 =====

/**
 * 获取仪表盘统计数据
 */
export function getDashboard(): Promise<DashboardStats> {
  return apiGet<DashboardStats>('/admin/dashboard')
}

// ===== 明细列表（员工 / 笔记） =====

/** 员工明细项 */
export interface EmployeeItem {
  id: string
  brand_id: string
  brand_name: string
  name: string
  status: string
  last_active_at: string | null
  /** 标签：official=正式 / test=测试，留空表示未分类 */
  tag: string
  /** 员工对外唯一标识（用于 h5 登录等） */
  employee_id: string
}

/** 员工明细列表响应 */
export interface EmployeeListResponse {
  total: number
  page: number
  page_size: number
  items: EmployeeItem[]
}

/**
 * 获取员工明细列表（后台）
 * @param brand_id 按品牌筛选
 * @param tag 按标签筛选：official=正式 / test=测试，留空=全部
 * @param page 页码
 * @param page_size 每页数量
 */
export function getEmployees(params: {
  brand_id?: string
  tag?: string
  page?: number
  page_size?: number
}): Promise<EmployeeListResponse> {
  const qs: Record<string, any> = {}
  if (params.brand_id) qs.brand_id = params.brand_id
  if (params.tag) qs.tag = params.tag
  if (params.page) qs.page = params.page
  if (params.page_size) qs.page_size = params.page_size
  return apiGet<EmployeeListResponse>('/admin/employees', qs)
}

/**
 * 更新员工（部分更新：标签 / 状态可单独传入）
 * @param id 员工记录 ID
 * @param data 需要变更的字段（tag / status，未传字段不变）
 * @returns 更新后的员工项
 */
export function updateEmployee(
  id: string,
  data: { tag?: string; status?: string },
): Promise<EmployeeItem> {
  return apiPatch<EmployeeItem>(`/admin/employees/${id}`, data)
}

/**
 * 批量删除员工
 * @param ids 员工记录 ID 数组
 * @returns 实际删除数量
 */
export function batchDeleteEmployees(ids: string[]): Promise<{ deleted: number }> {
  return apiPost<{ deleted: number }>('/admin/employees/batch-delete', { ids })
}

/** 额度消耗日志项 */
export interface QuotaLogItem {
  id: string
  brand_id: string
  brand_name: string
  employee_id: string
  employee_name: string
  /** 关联笔记 ID，可为 null */
  note_id: string | null
  /** 额度类型：generate=新建笔记 / text=换文案 / image=换图 / all=全换 */
  quota_type: 'generate' | 'text' | 'image' | 'all'
  /** 消耗点数 */
  consumed: number
  /** 创建时间（ISO 字符串） */
  created_at: string
}

/** 额度消耗日志列表响应 */
export interface QuotaLogListResponse {
  total: number
  page: number
  page_size: number
  items: QuotaLogItem[]
}

/**
 * 获取额度消耗明细列表（后台）
 * @param brand_id 按品牌筛选
 * @param employee_id 按员工筛选
 * @param page 页码
 * @param page_size 每页数量
 */
export function getQuotaLogs(params: {
  brand_id?: string
  employee_id?: string
  page?: number
  page_size?: number
}): Promise<QuotaLogListResponse> {
  const qs: Record<string, any> = {}
  if (params.brand_id) qs.brand_id = params.brand_id
  if (params.employee_id) qs.employee_id = params.employee_id
  if (params.page) qs.page = params.page
  if (params.page_size) qs.page_size = params.page_size
  return apiGet<QuotaLogListResponse>('/admin/quota-logs', qs)
}

/** 登录记录项 */
export interface LoginLogItem {
  id: string
  /** 员工 ID，可为 null（如老板入口未关联员工） */
  employee_id: string | null
  /** 员工姓名，可为 null 或 '-' */
  employee_name: string | null
  brand_id: string
  brand_name: string
  ip: string
  /** 已解析的可读设备串 */
  device: string
  /** 原始 User-Agent */
  user_agent: string
  /** 登录时间（ISO 字符串） */
  login_at: string
  /** 是否登录成功 */
  success: boolean
  /** 角色：employee=员工 / boss=老板 / 空串=未知 */
  role: 'employee' | 'boss' | ''
}

/** 登录记录列表响应 */
export interface LoginLogListResponse {
  total: number
  page: number
  page_size: number
  items: LoginLogItem[]
}

/**
 * 获取登录记录列表（后台）
 * @param brand_id 按品牌筛选
 * @param employee_id 按员工筛选
 * @param page 页码
 * @param page_size 每页数量
 */
export function getLoginLogs(params: {
  brand_id?: string
  employee_id?: string
  page?: number
  page_size?: number
}): Promise<LoginLogListResponse> {
  const qs: Record<string, any> = {}
  if (params.brand_id) qs.brand_id = params.brand_id
  if (params.employee_id) qs.employee_id = params.employee_id
  if (params.page) qs.page = params.page
  if (params.page_size) qs.page_size = params.page_size
  return apiGet<LoginLogListResponse>('/admin/login-logs', qs)
}

/** 笔记明细项 */
export interface NoteListItem {
  id: string
  brand_id: string
  brand_name: string
  employee_id: string
  employee_name: string
  module_type: string
  style_type: string
  title: string
  content: string
  tags: string[]
  status: string
  images: string[]
  xhs_note_url: string | null
  created_at: string | null
  published_at: string | null
}

/** 笔记明细列表响应 */
export interface NoteListResponse {
  total: number
  page: number
  page_size: number
  items: NoteListItem[]
}

/**
 * 获取笔记明细列表（后台）
 * @param brand_id 按品牌筛选
 * @param status 按状态筛选：draft / published
 * @param page 页码
 * @param page_size 每页数量
 */
export function getNotes(params: {
  brand_id?: string
  status?: string
  page?: number
  page_size?: number
}): Promise<NoteListResponse> {
  const qs: Record<string, any> = {}
  if (params.brand_id) qs.brand_id = params.brand_id
  if (params.status) qs.status = params.status
  if (params.page) qs.page = params.page
  if (params.page_size) qs.page_size = params.page_size
  return apiGet<NoteListResponse>('/admin/notes', qs)
}
